import { useResponderMap } from 'features/Responder/features/ResponderMap/contexts/ResponderMapContext';
import { useTours } from 'features/Responder/features/Tours';
import { useZones } from 'features/Responder/features/Zones';
import {
  centroid,
  estimateChildSeedSizeMeters,
  normalizePolygon,
} from 'features/Responder/features/Zones';
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';

const displayName = (zone) => zone?.name || `Zone #${zone?.id ?? '?'}`;

const ZoneExplorerTabButton = ({ active, onClick, children }) => (
  <Button
    size="sm"
    variant="outline-light"
    onClick={onClick}
    className={`px-3 zone-explorer-tab-btn ${active ? 'zone-explorer-tab-btn--active' : ''}`}
  >
    {children}
  </Button>
);

export default function ZoneTreePanel({
  maxHeight = '100%',
  zoneExplorerMode = 'tree',
  onSetZoneExplorerMode = () => {},
  onEditZone = () => {},
  onDeleteZone = () => {},
  showDeleteAction = true,
}) {
  const { zones, zonesWithMeta, activeZoneId, setActiveZoneId, setSelectedZoneIds } = useZones();
  const {
    selectedZoneIds,
    setSelectedZoneIds: setMapSelectedZoneIds,
    setShowZoneTools,
  } = useResponderMap();
  const { setCenter, setMapTool, mapTool } = useTours();
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const zonesList = useMemo(
    () => (Array.isArray(zonesWithMeta) && zonesWithMeta.length ? zonesWithMeta : zones || []),
    [zones, zonesWithMeta],
  );

  const zoneById = useMemo(
    () => new Map((Array.isArray(zonesList) ? zonesList : []).map((zone) => [`${zone?.id}`, zone])),
    [zonesList],
  );

  const pendingParentId = mapTool?.mode === 'zone' ? mapTool?.parentId : null;
  const pendingParentZone =
    pendingParentId != null ? zoneById.get(`${pendingParentId}`) || null : null;
  const awaitingShape =
    mapTool?.mode === 'zone' &&
    (!!mapTool?.awaitingShape || (!!pendingParentZone && !mapTool?.shape));

  const childMap = useMemo(() => {
    const map = new Map();
    zonesList.forEach((zone) => {
      const key = zone?.parentId == null ? '__root__' : `${zone.parentId}`;
      const list = map.get(key) || [];
      list.push(zone);
      map.set(key, list);
    });
    map.forEach((list, key) => {
      map.set(
        key,
        list.slice().sort((a, b) => displayName(a).localeCompare(displayName(b))),
      );
    });
    return map;
  }, [zonesList]);

  const rootZones = useMemo(() => {
    const directRoots = childMap.get('__root__') || [];
    const orphanRoots = zonesList.filter(
      (zone) => zone?.parentId != null && !zoneById.has(`${zone.parentId}`),
    );
    return [...directRoots, ...orphanRoots].filter(
      (zone, index, arr) => arr.findIndex((item) => `${item?.id}` === `${zone?.id}`) === index,
    );
  }, [childMap, zoneById, zonesList]);

  const expandableIds = useMemo(
    () =>
      zonesList
        .filter((zone) => {
          const children = childMap.get(`${zone?.id}`) || [];
          return children.length > 0;
        })
        .map((zone) => `${zone.id}`),
    [childMap, zonesList],
  );

  const selectedKeys = useMemo(
    () => new Set((Array.isArray(selectedZoneIds) ? selectedZoneIds : []).map((id) => `${id}`)),
    [selectedZoneIds],
  );

  useEffect(() => {
    const focusKey =
      activeZoneId != null
        ? `${activeZoneId}`
        : Array.isArray(selectedZoneIds) && selectedZoneIds.length
          ? `${selectedZoneIds[0]}`
          : null;
    if (!focusKey) return;
    const focusZone = zoneById.get(focusKey);
    if (!focusZone) return;
    const ancestors = [];
    let parentKey = focusZone.parentId != null ? `${focusZone.parentId}` : null;
    while (parentKey) {
      ancestors.push(parentKey);
      const parentZone = zoneById.get(parentKey);
      if (!parentZone || parentZone.parentId == null) break;
      parentKey = `${parentZone.parentId}`;
    }
    if (!ancestors.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      ancestors.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeZoneId, selectedZoneIds, zoneById]);

  const handleSelectZone = (zone) => {
    if (awaitingShape) return;
    if (!zone?.id) return;
    const firstLayerChildren = childMap.get(`${zone.id}`) || [];
    const selectedIds = [zone.id, ...firstLayerChildren.map((child) => child.id)].filter(
      (value) => value != null,
    );
    const uniqueIds = Array.from(new Set(selectedIds.map((value) => `${value}`)));
    setActiveZoneId(zone.id);
    setSelectedZoneIds(uniqueIds);
    setMapSelectedZoneIds(uniqueIds);
    if (firstLayerChildren.length) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(`${zone.id}`);
        return next;
      });
    }
    const points = [
      ...(Array.isArray(zone?._points) ? zone._points : zone?.polygon || []),
      ...firstLayerChildren.flatMap((child) =>
        Array.isArray(child?._points) ? child._points : child?.polygon || [],
      ),
    ];
    const center = centroid(points);
    if (center) {
      setCenter([center.lat, center.lng, 16]);
    }
  };

  const toggleExpanded = (zoneId) => {
    if (awaitingShape) return;
    setExpandedIds((prev) => {
      const key = `${zoneId}`;
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getZonePoints = (zone) => {
    if (!zone) return [];
    const points = Array.isArray(zone?._points) ? zone._points : zone?.polygon || [];
    return normalizePolygon(points);
  };

  const handleCreateZone = () => {
    if (awaitingShape) return;
    const parentZone =
      (activeZoneId != null ? zoneById.get(`${activeZoneId}`) : null) || rootZones?.[0] || null;
    const parentPoints = getZonePoints(parentZone);
    const parentSizeMeters = estimateChildSeedSizeMeters(parentPoints, 0.5, 60);

    setMapTool?.({
      mode: 'zone',
      parentId: parentZone?.id ?? null,
      parentName: displayName(parentZone),
      sizeMeters: parentSizeMeters,
      awaitingShape: true,
    });
    setShowZoneTools?.(true);
  };

  const renderNode = (zone, depth = 0) => {
    const idKey = `${zone.id}`;
    const children = childMap.get(idKey) || [];
    const isExpanded = expandedIds.has(idKey);
    const isSelected = selectedKeys.has(idKey);
    const isActive = activeZoneId != null && `${activeZoneId}` === idKey;

    return (
      <div key={idKey}>
        <div
          className={`zone-tree-node responder-interactive-item d-flex align-items-center gap-2 ${
            isSelected ? 'is-selected zone-tree-node-selected' : 'zone-tree-node-default'
          }`}
          style={{ marginLeft: `${depth * 12}px`, cursor: 'pointer' }}
          onClick={() => handleSelectZone(zone)}
        >
          {children.length > 0 ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-light py-0 px-1"
              onClick={(event) => {
                event.stopPropagation();
                toggleExpanded(zone.id);
              }}
              aria-label={isExpanded ? 'Collapse zone children' : 'Expand zone children'}
            >
              {isExpanded ? 'v' : '>'}
            </button>
          ) : (
            <span style={{ width: 22, display: 'inline-block' }} />
          )}
          <div className="d-flex flex-column">
            <span className="fw-semibold">{displayName(zone)}</span>
            <span className="small text-muted">
              Depth {Number.isFinite(zone?.depth) ? zone.depth : 0}
              {children.length
                ? ` - ${children.length} child${children.length === 1 ? '' : 'ren'}`
                : ''}
            </span>
          </div>
          <div className="ms-auto d-flex gap-1 align-items-center">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="responder-tooltip" id={`zone-tree-edit-${idKey}`}>
                  Edit zone
                </Tooltip>
              }
            >
              <button
                type="button"
                className="zone-inline-action responder-interactive-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditZone(zone);
                }}
                aria-label="Edit zone"
              >
                <FaEdit size={11} />
              </button>
            </OverlayTrigger>
            {showDeleteAction && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip className="responder-tooltip" id={`zone-tree-delete-${idKey}`}>
                    Delete zone
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className="zone-inline-action responder-interactive-action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteZone(zone);
                  }}
                  aria-label="Delete zone"
                >
                  <FaTrash size={11} />
                </button>
              </OverlayTrigger>
            )}
            {isActive && <Badge bg="primary">Active</Badge>}
            {children.length > 0 && <Badge bg="secondary">{children.length}</Badge>}
          </div>
        </div>
        {isExpanded && children.length > 0 && (
          <div className="d-flex flex-column gap-1 mt-1">
            {children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="shadow-sm h-100">
      <Card.Body className="d-flex flex-column p-2" style={{ minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <div className="d-flex flex-column gap-1">
            <div className="zone-panel-heading-copy">
              <Card.Title className="mb-0 fs-6">Zone Tree</Card.Title>
              <Card.Text className="text-muted small mb-0">
                Click a zone to focus it and its first-layer children.
              </Card.Text>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'list'}
                onClick={() => onSetZoneExplorerMode('list')}
              >
                Zone List
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'tree'}
                onClick={() => onSetZoneExplorerMode('tree')}
              >
                Zone Tree
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'links'}
                onClick={() => onSetZoneExplorerMode('links')}
              >
                Zone Links
              </ZoneExplorerTabButton>
            </div>
            {awaitingShape && (
              <Card.Text className="small mb-0 zone-tree-lock-hint">
                Shape selection in progress. Pick a shape in Zone Tools or press <kbd>Esc</kbd> to
                cancel.
              </Card.Text>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="primary" onClick={handleCreateZone} disabled={awaitingShape}>
              Create
            </Button>
            <Badge bg="secondary">{zonesList.length}</Badge>
          </div>
        </div>

        <div className="d-flex gap-2 mb-2">
          <Button
            size="sm"
            variant="outline-light"
            className="responder-interactive-action zone-tree-expand-action"
            disabled={awaitingShape || !expandableIds.length}
            onClick={() => setExpandedIds(new Set(expandableIds))}
          >
            Expand all
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-interactive-action"
            disabled={awaitingShape || !expandedIds.size}
            onClick={() => setExpandedIds(new Set())}
          >
            Collapse all
          </Button>
        </div>

        <div
          className={`zone-tree-items flex-grow-1 overflow-auto d-flex flex-column gap-1 ${
            awaitingShape ? 'zone-tree-items-locked' : ''
          }`}
          style={{ maxHeight }}
        >
          {rootZones.length > 0 ? (
            rootZones.map((zone) => renderNode(zone, 0))
          ) : (
            <div className="text-muted small">No zones available.</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}


